import { Fragment, useEffect, useMemo, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { TEXT_STRING } from '../../constants';
import { Button, Icon, Label, Modal, MultipleSelect, Rating, Text } from 'components';
import { TActivityArea, TFilterMale, TSelectOption } from 'types';
import { GuestRankStarDisplay } from './GuestRankStarDisplay';

const LIST_MALE_STR = TEXT_STRING.LIST_MALE;
const COMMON_STR = TEXT_STRING.COMMON;
const SEARCH_PAGE_STR = TEXT_STRING.SEARCH_PAGE;

type FilterGuestRankProps = {
  selected: number[];
  setSelected: (value: number[]) => void;
};

function FilterGuestRank({ selected, setSelected }: FilterGuestRankProps) {
  const starOptions = useMemo(
    () => Array.from({ length: 6 }, (_, index) => index).reverse(),
    [],
  );

  return (
    <div className="mb-6">
      <Label className="mb-2" color="text-neutral">
        {LIST_MALE_STR.FILTER.GUEST_RANK}
      </Label>
      <Listbox value={selected} onChange={setSelected} multiple>
        <div className="relative">
          <Listbox.Button className="min-h-[40px] rounded-2 p-[12px] relative w-full cursor-default ring-1 ring-inset ring-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary sm:leading-6">
            <div className="flex flex-wrap pr-[18px]">
              {selected.length ? (
                selected.map((item, index) => (
                  <div className="flex flex-wrap items-center" key={`${item}-${index}`}>
                    <GuestRankStarDisplay star={item} />
                    {index < selected.length - 1 ? (
                      <p className="leading-[16px] px-[4px]"> - </p>
                    ) : null}
                  </div>
                ))
              ) : (
                <Text textColor="text-neutral-300">{COMMON_STR.SELECT}</Text>
              )}
            </div>

            <span className="pointer-events-none absolute inset-y-0 right-2 ml-3 mb-1 flex items-center pr-2 -rotate-90 scale-75">
              <Icon icon="chevronLeft" color="black" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {starOptions.map((star) => (
                <Listbox.Option
                  key={star}
                  value={star}
                  className="group flex cursor-default items-center gap-2 py-2 px-[12px] select-none data-[focus]:bg-white/10"
                >
                  {({ selected: isSelected }) => (
                    <>
                      <Rating value={star} />
                      {isSelected ? (
                        <span className="inset-y-0 left-0 flex items-center text-amber-600">
                          <Icon icon="check" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}

function MaleFilterModal({
  filter,
  openModal,
  activityAreas,
  closeModal,
  handleSearch,
}: {
  openModal: boolean;
  filter: TFilterMale;
  activityAreas: TActivityArea[];
  closeModal: () => void;
  handleSearch: (body: TFilterMale) => void;
}) {
  const { areaIds: filteredAreaIds = [], stars: filteredStar = [] } = filter;

  const options = useMemo<TSelectOption[]>(
    () =>
      activityAreas?.map((item) => ({
        name: item.name,
        value: `${item.id}`,
      })) || [],
    [activityAreas],
  );

  const [areaIds, setAreaIds] = useState<number[]>(filteredAreaIds);
  const [guestRanks, setGuestRanks] = useState<number[]>(filteredStar);

  useEffect(() => {
    if (!openModal) return;
    setAreaIds(filteredAreaIds);
    setGuestRanks(filteredStar);
  }, [filteredAreaIds, filteredStar, openModal]);

  const handleClose = () => {
    closeModal();
  };

  return (
    <Modal open={openModal} className="p-5 bg-white" onClose={handleClose}>
      <div className="flex flex-col gap-5 min-h-[25dvh] max-h-[85dvh]">
        <div onClick={handleClose} className="absolute top-4 cursor-pointer">
          <Icon icon="close" />
        </div>
        <Text center>{LIST_MALE_STR.FILTER_TITLE}</Text>

        <div className="overflow-y-auto">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2 flex-1">
              <Text textColor="text-neutral">{LIST_MALE_STR.FILTER.AREA}</Text>
              <MultipleSelect
                name="listArea"
                values={options.filter((option) => areaIds.includes(+option.value))}
                onChange={(data) => setAreaIds(data.map((item) => +item.value))}
                options={options}
              />
            </div>
            <FilterGuestRank selected={guestRanks} setSelected={setGuestRanks} />
          </div>
        </div>

        <Button
          className="w-full py-5"
          textSize="text-sm"
          onClick={() =>
            handleSearch({
              areaIds,
              stars: guestRanks,
            })
          }
        >
          {SEARCH_PAGE_STR.SEARCH}
        </Button>
      </div>
    </Modal>
  );
}

export default MaleFilterModal;
